import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import NavigatorManageTable, {
  nameSort,
  tokenizedNameSearch,
  SimpleActionsMenu,
} from './NavigatorManageTable';

// Thin wrapper over the shared NavigatorManageTable for the Exercises page's
// Progressions tab — mirrors ManageTagsTable's structure.

const NameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
`;

const BULK_ACTIONS = [
  {
    key: 'deleteProgressions', label: 'Delete', icon: 'delete', iconColor: '#FF0505',
  },
];

const NameCell = ({ row }) => {
  const { name } = row.original;
  return <NameText title={name}>{name || 'Untitled progression'}</NameText>;
};

NameCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.shape({ name: PropTypes.string }) }).isRequired,
};

const ProgressionActionsCell = ({ row }) => {
  const p = row.original;
  return <SimpleActionsMenu onEdit={() => p.onEdit(p)} onDelete={() => p.onDelete(p)} />;
};

ProgressionActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onEdit: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

const levelCount = (p) => Number(p.levels) || 0;

const ManageProgressionsTable = ({
  progressions, isLoading, hasError, onRetry, onEditProgression, onDeleteProgression,
  onBulkDelete, clearSelectionToken,
}) => {
  const tableData = React.useMemo(
    () => (progressions || []).map((p) => ({
      ...p, onEdit: onEditProgression, onDelete: onDeleteProgression,
    })),
    [progressions, onEditProgression, onDeleteProgression],
  );

  const columns = React.useMemo(() => [
    {
      Header: 'Progression',
      accessor: 'name',
      sortType: nameSort,
      headerStyle: { minWidth: '280px', flex: '1' },
      cellStyle: { minWidth: '280px', flex: '1' },
      Cell: NameCell,
    },
    {
      Header: 'Number of Blocks',
      id: 'levels',
      accessor: levelCount,
      headerStyle: { flex: '1', minWidth: '180px', textAlign: 'center' },
      cellStyle: { flex: '1', minWidth: '180px', textAlign: 'center' },
    },
    {
      Header: 'Actions',
      id: 'actions',
      sticky: 'right',
      disableSortBy: true,
      headerStyle: { width: '90px', textAlign: 'center' },
      cellStyle: { width: '90px', textAlign: 'center' },
      Cell: ProgressionActionsCell,
    },
  ], []);

  const handleBulkAction = (key, selectedIds) => {
    if (key === 'deleteProgressions') onBulkDelete(selectedIds);
  };

  return (
    <NavigatorManageTable
      data={tableData}
      columns={columns}
      isLoading={isLoading}
      hasError={hasError}
      errorMessage='Something went wrong loading progressions.'
      onRetry={onRetry}
      searchKeys={['name']}
      searchFn={tokenizedNameSearch}
      searchPlaceholder='Search for a progression'
      emptyMessage='No progressions yet.'
      searchEmptyMessage='No progressions match your search.'
      entityLabel='progression'
      defaultSortBy={[{ id: 'name', desc: false }]}
      bulkActions={BULK_ACTIONS}
      onBulkAction={handleBulkAction}
      clearSelectionToken={clearSelectionToken}
      virtualizeRows
    />
  );
};

ManageProgressionsTable.propTypes = {
  progressions: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  onRetry: PropTypes.func,
  onEditProgression: PropTypes.func,
  onDeleteProgression: PropTypes.func,
  onBulkDelete: PropTypes.func,
  clearSelectionToken: PropTypes.number,
};

ManageProgressionsTable.defaultProps = {
  progressions: [],
  isLoading: false,
  hasError: false,
  onRetry: () => {},
  onEditProgression: () => {},
  onDeleteProgression: () => {},
  onBulkDelete: () => {},
  clearSelectionToken: 0,
};

export default ManageProgressionsTable;
